import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserAssistant, createOrUpdateAssistant, supabase } from '../services/supabase';
import { AssistantFormData } from '../types';
import { generateUniqueUrlWithCheck, sanitizeUrlHandle, checkUrlHandleUniqueness, getCurrentUrlPrefix } from '../utils/helpers';
import { handleSupabaseError } from '../utils/errorHandler';
import { DEFAULT_COLORS, VALIDATION, ROUTES, PRONOUN_OPTIONS } from '../constants';
import { getCharacterCountColor, validateField } from '../utils/dashboardUtils';
import { generatePersonalContextCategories, PersonalContextCategory } from '../services/openai';
import {
    ArrowLeft,
    Save,
    Sparkles,
    User,
    CheckCircle,
    Lightbulb,
} from 'lucide-react';

const CreateAssistant: React.FC = () => {
    const navigate = useNavigate();
    const { assistantId } = useParams<{ assistantId: string }>();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState<AssistantFormData>({
        name: '',
        pronouns: '',
        primary_color: DEFAULT_COLORS.PRIMARY,
        secondary_color: DEFAULT_COLORS.SECONDARY,
        unique_url: '',
    });
    const [learningObjective, setLearningObjective] = useState('');

    const checkExistingAssistant = useCallback(async () => {
        if (!user) return;

        try {
            const existingAssistant = await getUserAssistant(user.id);
            if (existingAssistant) {
                // User already has an assistant, redirect to edit mode
                navigate(`/edit-assistant/${existingAssistant.id}`);
            }
        } catch (error) {
            console.error('Error checking existing assistant:', error);
        }
    }, [user, navigate]);

    const fetchAssistantData = useCallback(async () => {
        if (!user) return;

        try {
            const assistant = await getUserAssistant(user.id);
            if (assistant) {
                setFormData({
                    name: assistant.name,
                    pronouns: assistant.pronouns || '',
                    primary_color: assistant.primary_color,
                    secondary_color: assistant.secondary_color,
                    unique_url: assistant.unique_url,
                });
                // Note: learningObjective would need to be stored in the database
                // For now, we'll leave it empty in edit mode
            }
        } catch (error) {
            console.error('Error fetching assistant data:', error);
        }
    }, [user]);

    useEffect(() => {
        if (assistantId && user) {
            setIsEditMode(true);
            fetchAssistantData();
        } else if (user && !assistantId) {
            // Check if user already has an assistant when creating new one
            checkExistingAssistant();
        }
    }, [assistantId, user, checkExistingAssistant, fetchAssistantData]);



    const validateForm = (): string | null => {
        if (formData.name.length < VALIDATION.MIN_NAME_LENGTH) {
            return `Name must be at least ${VALIDATION.MIN_NAME_LENGTH} characters long`;
        }
        if (formData.name.length > VALIDATION.MAX_NAME_LENGTH) {
            return `Name must be no more than ${VALIDATION.MAX_NAME_LENGTH} characters long`;
        }
        if (!formData.pronouns) {
            return 'Please select pronouns';
        }
        if (!PRONOUN_OPTIONS.includes(formData.pronouns as any)) {
            return 'Please select a valid pronoun option';
        }
        if (!learningObjective.trim()) {
            return 'Please describe what you want people to learn about you';
        }
        if (learningObjective.length < 10) {
            return 'Learning objective must be at least 10 characters long';
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validationError = validateForm();
        if (validationError) {
            alert(validationError);
            return;
        }

        if (!user) {
            alert('You must be logged in to create an assistant');
            return;
        }

        setLoading(true);
        try {
            // Generate personal context categories automatically
            let categories: PersonalContextCategory[] = [];
            try {
                categories = await generatePersonalContextCategories(formData.name, learningObjective);
                console.log('Generated categories:', categories);
            } catch (categoryError) {
                console.error('Error generating categories:', categoryError);
                // Continue with assistant creation even if category generation fails
            }

            // Validate and sanitize URL handle
            let finalUrlHandle = formData.unique_url;

            if (!isEditMode) {
                // For new assistants, generate URL handle if not provided
                if (!formData.unique_url.trim()) {
                    finalUrlHandle = await generateUniqueUrlWithCheck(formData.name, supabase);
                } else {
                    // Validate and sanitize user-provided URL handle
                    const urlHandleValidationError = validateField('unique_url', formData.unique_url);
                    if (urlHandleValidationError) {
                        alert(urlHandleValidationError);
                        return;
                    }

                    const sanitizedUrlHandle = sanitizeUrlHandle(formData.unique_url);

                    // Check uniqueness
                    const isUnique = await checkUrlHandleUniqueness(sanitizedUrlHandle, supabase);
                    if (!isUnique) {
                        alert('This URL handle is already taken. Please choose a different one.');
                        return;
                    }

                    finalUrlHandle = sanitizedUrlHandle;
                }
            } else {
                // For editing, validate and check uniqueness if changed
                const currentAssistant = await getUserAssistant(user.id);
                if (currentAssistant && formData.unique_url !== currentAssistant.unique_url) {
                    const urlHandleValidationError = validateField('unique_url', formData.unique_url);
                    if (urlHandleValidationError) {
                        alert(urlHandleValidationError);
                        return;
                    }

                    const sanitizedUrlHandle = sanitizeUrlHandle(formData.unique_url);

                    // Check uniqueness
                    const isUnique = await checkUrlHandleUniqueness(sanitizedUrlHandle, supabase, user.id);
                    if (!isUnique) {
                        alert('This URL handle is already taken. Please choose a different one.');
                        return;
                    }

                    finalUrlHandle = sanitizedUrlHandle;
                }
            }

            const assistantData = {
                name: formData.name,
                pronouns: formData.pronouns,
                primary_color: formData.primary_color,
                secondary_color: formData.secondary_color,
                unique_url: finalUrlHandle,
                // Note: learningObjective and generatedCategories would need to be stored
                // This might require database schema changes
            };

            const result = await createOrUpdateAssistant(user.id, assistantData, isEditMode, true);

            if (result) {
                // Store the generated categories for later use in personal context
                if (categories.length > 0) {
                    // You could store these in localStorage or pass them to the dashboard
                    localStorage.setItem('generatedCategories', JSON.stringify(categories));
                    localStorage.setItem('learningObjective', learningObjective);
                }
                // Redirect to build story page instead of overview
                navigate(`${ROUTES.DASHBOARD}?page=build-story`);
            } else {
                // Check if user already has an assistant
                const existingAssistant = await getUserAssistant(user.id);
                if (existingAssistant) {
                    throw new Error('You already have an assistant. Please edit your existing assistant instead.');
                } else {
                    throw new Error('Failed to create/update assistant');
                }
            }
        } catch (error) {
            console.error('Error creating/updating assistant:', error);
            if (error instanceof Error) {
                const errorMessage = handleSupabaseError(error, 'CreateAssistant');
                alert(errorMessage);
            } else {
                alert('Failed to create/update assistant. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const isFieldValid = (field: string, value: string): boolean => {
        switch (field) {
            case 'name':
                return value.length >= VALIDATION.MIN_NAME_LENGTH && value.length <= VALIDATION.MAX_NAME_LENGTH;
            case 'pronouns':
                return Boolean(value && PRONOUN_OPTIONS.includes(value as any));
            case 'learningObjective':
                return value.length >= 10;
            default:
                return true;
        }
    };

    const learningObjectiveExamples = [
        "Showcases my expertise, work, and professional journey",
        "Documents my personal stories, memories, and the things that make me me",
        "Captures my artistic journey, creative process, and the inspiration behind my work",
        "Highlights my achievements, skills, and what I can offer to others",
        "Shares my life experiences, lessons learned, and personal growth",
        "Introduces my background, interests, and what drives me professionally"
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200/50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-4">
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => navigate(ROUTES.DASHBOARD)}
                                className="flex items-center text-slate-600 hover:text-slate-900 transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5 mr-2" />
                                Back to Dashboard
                            </button>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">
                                    {isEditMode ? 'Edit Assistant' : 'Create Your Echoo'}
                                </h1>
                                <p className="text-sm text-slate-600">
                                    {isEditMode ? 'Update your assistant settings' : 'Set up your AI assistant'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Information */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center mb-6">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                                    <User className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-slate-900">Who do you want people to learn about?</h2>
                                </div>
                            </div>

                            <div className="space-y-6">
                                {/* Name Field */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Echoo Name:
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        placeholder="Enter name..."
                                        maxLength={VALIDATION.MAX_NAME_LENGTH}
                                        required
                                    />
                                    <div className="flex items-center justify-between mt-2">
                                        <p className={`text-sm ${getCharacterCountColor(formData.name.length, VALIDATION.MAX_NAME_LENGTH)}`}>
                                            {formData.name.length}/{VALIDATION.MAX_NAME_LENGTH} characters
                                        </p>
                                        {formData.name.length >= VALIDATION.MIN_NAME_LENGTH && (
                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                        )}
                                    </div>
                                    {formData.name.length > 0 && formData.name.length < VALIDATION.MIN_NAME_LENGTH && (
                                        <p className="text-sm text-red-500 mt-1">
                                            Name must be at least {VALIDATION.MIN_NAME_LENGTH} characters
                                        </p>
                                    )}
                                </div>

                                {/* Pronouns Field */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Pronouns:
                                    </label>
                                    <select
                                        value={formData.pronouns}
                                        onChange={(e) => setFormData(prev => ({ ...prev, pronouns: e.target.value }))}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        required
                                    >
                                        <option value="">Select pronouns...</option>
                                        {PRONOUN_OPTIONS.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* URL Handle Field */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        URL Handle:
                                    </label>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm text-slate-500">{getCurrentUrlPrefix()}</span>
                                        <input
                                            type="text"
                                            value={formData.unique_url}
                                            onChange={(e) => setFormData(prev => ({ ...prev, unique_url: e.target.value }))}
                                            className="flex-1 px-4 py-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                            placeholder="your-handle"
                                            maxLength={50}
                                        />
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">
                                        This will be your unique URL. Only lowercase letters, numbers, and hyphens are allowed.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Learning Objective */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center mb-6">
                                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                                    <Lightbulb className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-slate-900">What do you want people to discover about {formData.name || 'you'}?</h2>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        What do you want people to learn about {formData.name || 'you'}?
                                    </label>
                                    <textarea
                                        value={learningObjective}
                                        onChange={(e) => setLearningObjective(e.target.value)}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                                        rows={4}
                                        placeholder="Describe what you want people to learn about you..."
                                        maxLength={500}
                                        required
                                    />
                                    <div className="flex items-center justify-between mt-2">
                                        <p className={`text-sm ${getCharacterCountColor(learningObjective.length, 500)}`}>
                                            {learningObjective.length}/500 characters
                                        </p>
                                        {learningObjective.length >= 10 && (
                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                        )}
                                    </div>
                                </div>

                                {/* Example inspirations */}
                                <div className="bg-slate-50 rounded-lg p-4">
                                    <h4 className="text-sm font-medium text-slate-700 mb-3">Need inspiration? Try these examples:</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {learningObjectiveExamples.map((example, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => setLearningObjective(example)}
                                                className="text-left text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded px-3 py-2 transition-colors"
                                            >
                                                {example}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex items-center justify-end space-x-4">
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.DASHBOARD)}
                            className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !isFieldValid('name', formData.name) || !isFieldValid('pronouns', formData.pronouns) || !isFieldValid('learningObjective', learningObjective)}
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                    {isEditMode ? 'Updating...' : 'Creating Echoo...'}
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4 mr-2" />
                                    {isEditMode ? 'Update Assistant' : 'Create Echoo'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
};

export default CreateAssistant;
